import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslator } from 'tradera/lang/translate';
import { LocalLoader } from 'tradera/components/loader/loader';
import { BidsHistoryTable } from 'tradera/components/bid-history/bid-history-table';
import { selectPreferredCurrency } from 'tradera/state/multi-currency/selectors';
import { Button } from 'tradera/components/atoms/button/button';
import { ScrollableModal } from 'tradera/components/scrollable-modal/scrollable-modal';
import { useGetBidHistoryQuery } from './bid-history-api';
import { classNames } from 'tradera/utils/class-names';

type BidHistoryModalProps = {
    itemId: number;
    show: boolean;
    onClose: () => void;
    className?: string;
    isBuyer: boolean;
};

export const BidHistoryModal = ({
    itemId,
    show,
    onClose,
    className,
    isBuyer
}: BidHistoryModalProps) => {
    const [currencyIsSEK, setCurrencyIsSEK] = useState(false);
    const preferredCurrency = useSelector(selectPreferredCurrency);

    const { t } = useTranslator();

    const { data, isLoading, isError, isSuccess } = useGetBidHistoryQuery(
        itemId,
        {
            skip: !itemId || !show
        }
    );
    const { bids, auctionOpeningBid, auctionStartDate, payment } = data ?? {
        auctionOpeningBid: 0,
        auctionStartDate: ''
    };

    const currency =
        payment?.paymentMethodExchangeRate?.currencyCode ||
        preferredCurrency.code;

    const currencyInfoButton = (
        <Button
            theme="link"
            onClick={() => {
                setCurrencyIsSEK(!currencyIsSEK);
            }}
        />
    );

    const bidderIds = new Set(bids?.map((bid) => bid.memberId));
    const biddersCount = bidderIds.size ?? 0;
    const bidsCount = bids?.length ?? 0;

    return (
        <ScrollableModal
            show={show}
            maxWidth="50rem"
            mobileBottomSheet
            onClose={onClose}
            title={t('bidHistory_title')}
            footer={
                <Button fluid onClick={onClose}>
                    {t('bidHistory_close')}
                </Button>
            }>
            {isSuccess ? (
                <div>
                    <p>
                        <span className="text-no-wrap font-hansen">
                            {t('bidHistory_BidsWithCount', {
                                count: bidsCount
                            })}
                            ∙
                            {t('bidHistory_numberOfBiddersWithCount', {
                                count: biddersCount
                            })}
                        </span>{' '}
                        {currency !== 'SEK' && (
                            <span className="text-muted text-no-wrap">
                                {currencyIsSEK ? (
                                    <Trans
                                        i18nKey="bidHistory_currencyInfoButton_Trans"
                                        components={{
                                            button: currencyInfoButton
                                        }}
                                        values={{ currency }}
                                    />
                                ) : (
                                    <Trans
                                        i18nKey="bidHistory_currencyInfoButton_showInSEK_Trans"
                                        components={{
                                            button: currencyInfoButton
                                        }}
                                        values={{ currency }}
                                    />
                                )}
                            </span>
                        )}
                    </p>
                    <BidsHistoryTable
                        bids={bids}
                        auctionOpeningBid={auctionOpeningBid}
                        auctionStartDate={auctionStartDate}
                        className={classNames('w-100', className)}
                        currencyIsSEK={currencyIsSEK}
                        payment={payment}
                        isBuyer={isBuyer}
                    />
                </div>
            ) : isLoading ? (
                <div
                    className="position-relative mt-4"
                    style={{ height: '20rem' }}>
                    <LocalLoader />
                </div>
            ) : isError ? (
                <div
                    className="text-center d-flex flex-column justify-content-center"
                    style={{ height: '200px' }}>
                    <h5 className="text-danger">
                        {t('bidHistory_errorOccurred')}
                    </h5>
                    <p>{t('bidHistory_errorTryLater')}</p>
                </div>
            ) : null}
        </ScrollableModal>
    );
};
